import Button from "@amzn/meridian/button";
import Loader from "@amzn/meridian/loader";
import Textarea from "@amzn/meridian/textarea";
import { formatISO } from "date-fns";
import { useEffect, useRef } from "react";
import ChatHandleSubmitStreaming from "../chatHandleSubmitStreaming";
import ChatCards from "src/components/pages/home/components/chat/chatCards/chatCards";
import ChatResult from "src/components/pages/home/components/chat/chatResult/chatResult";
import StreamingMessage from "src/components/pages/home/components/chat/streamingMessage/streamingMessage";
import ChatService from "src/components/pages/home/service/chatService";
import {
  setAllFavoriteMessages,
  setErrorMessage,
  setInputText,
  setIsFavoriteMessagesLoaded,
  setIsRecentChatsLoaded,
  setRecentChats,
  setSite,
} from "src/components/pages/home/store/chatSlice";
import UserMessage from "src/components/shared/userMessage/userMessage";
import {
  DEFAULT_SITE,
  TOAST_TIMEOUT,
  MAX_INPUT_LENGTH,
} from "src/constants/constants";
import sendIcon from "src/images/send_icon.png";
import ParseService from "src/services/parseService";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import ChatHandleSubmit from "../chatHandleSubmit";
import "./chat.scss";
import FavoriteToggleButton from "src/components/pages/home/components/chat/favoriteToggleButton";
import { Column } from "@amzn/meridian";
import CustomUserMessage from "src/components/shared/userMessage/customUserMessage";

interface ChatProps {
  isOnMcpRoute: boolean;
}
const Chat = ({ isOnMcpRoute }: ChatProps) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef(null);

  const {
    inputText,
    isLoading,
    isInputDisabled,
    showResult,
    errorMessage,
    responses,
  } = useAppSelector((state) => state.chatSlice);
  const { firstName, alias, token, loc_descr, isAuthorized } = useAppSelector(
    (state) => state.user,
  );

  useEffect(() => {
    const fetchSite = async () => {
      if (isAuthorized && loc_descr !== "") {
        try {
          const resultSite = await ParseService.getSite(loc_descr);
          dispatch(setSite(resultSite));
        } catch (error) {
          dispatch(setSite(DEFAULT_SITE));
        }
      }
    };

    fetchSite();
  }, [token]);

  useEffect(() => {
    if (isAuthorized && alias !== "") {
      const fetchChatHistory = async () => {
        dispatch(setIsRecentChatsLoaded(false));
        try {
          const response = await ChatService.getRecentChats();
          const favoriteMessages = await ChatService.getAllFavorites();

          dispatch(setAllFavoriteMessages(favoriteMessages));
          dispatch(setRecentChats(response.data.recent_messages));
          dispatch(setIsRecentChatsLoaded(true));
          dispatch(setIsFavoriteMessagesLoaded(true));
        } catch (err) {
          dispatch(setErrorMessage("Failed to fetch chat history"));
        }
      };

      fetchChatHistory();
    }
  }, [alias]);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    setTimeout(function () {
      inputRef.current.focus();
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, [responses]);

  useEffect(() => {
    if (errorMessage) {
      dispatch(
        createToast({
          type: "error",
          message: errorMessage,
          timeout: TOAST_TIMEOUT,
        }),
      );
      inputRef.current.focus();
    }
  }, [errorMessage]);

  const inputTextArea = document.getElementById("chat-input-text-area");
  if (inputTextArea) {
    inputTextArea.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        document.getElementById("chat-input-button").click();
        e.stopImmediatePropagation();
        e.stopPropagation();
      }
    });
  }

  const chatInput = (
    <div
      className={
        isLoading && responses.length === 0 ? "chat-input-form_onLoading" : ""
      }
    >
      <div className="chat-search-input">
        <Textarea
          id="chat-input-text-area"
          ref={inputRef}
          value={inputText}
          onChange={(inputText) => {
            const truncatedText =
              inputText.length > MAX_INPUT_LENGTH
                ? inputText.substring(0, MAX_INPUT_LENGTH)
                : inputText;
            dispatch(setInputText(truncatedText));
          }}
          placeholder="Enter a prompt here"
          resize="auto"
          rows={5}
          disabled={isInputDisabled}
          error={
            inputText.length >= MAX_INPUT_LENGTH ||
            (inputText.trim() === "" && inputText !== "")
          }
          errorMessage={
            inputText.length >= MAX_INPUT_LENGTH
              ? `Maximum ${MAX_INPUT_LENGTH} characters allowed`
              : inputText.trim() === "" && inputText !== ""
                ? "Question cannot be empty, please enter a valid one"
                : undefined
          }
        />
        <Button
          id="chat-input-button"
          type="icon"
          onClick={
            isOnMcpRoute
              ? ChatHandleSubmitStreaming(inputText.trim())
              : ChatHandleSubmit(inputText.trim())
          }
          disabled={inputText.trim() === "" || isInputDisabled}
        >
          {isLoading! ? (
            <Loader type="circular" size="medium" />
          ) : (
            <img
              src={sendIcon}
              alt="Safer Search Send Icon"
              className="chat-search-send-icon"
            />
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <Column
      className={
        responses.length === 0 && isLoading
          ? "blank_main_chat_page"
          : "main_chat_page"
      }
    >
      {showResult ? (
        <Column
          alignmentVertical="justify"
          spacingInset={"500 none"}
          minWidth={768}
          minHeight="calc(100vh - 48px)"
        >
          {" "}
          <Column className="result" spacing={"300"}>
            {responses.length !== 0 &&
              responses.map((output) => (
                <div
                  key={output.messageId}
                  id={output.messageId}
                  className="message-container"
                >
                  <div className="user-message-row">
                    <CustomUserMessage
                      className="user_message_wrapper"
                      alias={firstName}
                      datetime={output.datetime || formatISO(new Date())}
                    >
                      {output.userText}
                    </CustomUserMessage>
                    <FavoriteToggleButton messageId={output.messageId} />
                  </div>
                  <ChatResult output={output} isOnMcpRoute={isOnMcpRoute} />
                </div>
              ))}
            {isOnMcpRoute && <StreamingMessage />}
          </Column>
          {chatInput}
        </Column>
      ) : (
        chatInput
      )}

      {!showResult && (
        <ChatCards inputRef={inputRef} isOnMcpRoute={isOnMcpRoute} />
      )}
    </Column>
  );
};

export default Chat;
